'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocalStorageState } from '@morpho-org/dapp-core';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import type { AdlSunsetNudgeSource } from '@/lib/analytics/client/events';
import { trackAdlSunsetNudgeViewed } from '@/lib/analytics/client/trackers/adl-sunset-nudge';
interface UseAdlSunsetNudgeParams {
  /** Per-surface localStorage key. The nudge opens at most once per key. */
  dismissalKey: string;
  source: AdlSunsetNudgeSource;
  /** Market context, forwarded to the impression event on the market surface. */
  chainId?: number;
  marketId?: string;
  /**
   * Builds the dialog element to render. Read through a ref so a fresh closure
   * each render does not churn `requestOpen`'s identity — detectors depend on
   * it and would re-fire (and re-open the nudge) on every render otherwise.
   */
  renderDialog: () => ReactNode;
}

/**
 * Shared core for the Auto-Deleverage sunset nudge, used by both the dashboard
 * watcher (fans out one detector per borrow position) and the market-page
 * watcher (single market). Owns: open-once, defer-if-another-dialog-is-open +
 * drain-on-close, per-surface dismissal persistence, and the impression event.
 *
 * Callers gate the detector(s) on `dismissed` and call `requestOpen` when a
 * position reports Auto-Deleverage authorized.
 */
export const useAdlSunsetNudge = t0 => {
  const $ = _c(21);
  const {
    dismissalKey,
    source,
    chainId,
    marketId,
    renderDialog
  } = t0;
  const {
    toggleDialog,
    isOpen
  } = useDialogContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      defaultValue: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [dismissed, setDismissed] = useLocalStorageState(dismissalKey, t1);
  const renderDialogRef = useRef(renderDialog);
  let t2;
  if ($[1] !== renderDialog) {
    t2 = () => {
      renderDialogRef.current = renderDialog;
    };
    $[1] = renderDialog;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t2);
  const isOpenRef = useRef(isOpen);
  let t3;
  let t4;
  if ($[3] !== isOpen) {
    t3 = () => {
      isOpenRef.current = isOpen;
    };
    t4 = [isOpen];
    $[3] = isOpen;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  const hasOpenedRef = useRef(false);
  const pendingOpenRef = useRef(false);
  let t5;
  if ($[6] !== chainId || $[7] !== marketId || $[8] !== setDismissed || $[9] !== source || $[10] !== toggleDialog) {
    t5 = () => {
      if (hasOpenedRef.current) {
        return;
      }
      hasOpenedRef.current = true;
      pendingOpenRef.current = false;
      setDismissed(true);
      trackAdlSunsetNudgeViewed({
        source,
        chainId,
        marketId
      });
      toggleDialog(renderDialogRef.current(), {
        closable: true,
        maxWidth: "480px"
      });
    };
    $[6] = chainId;
    $[7] = marketId;
    $[8] = setDismissed;
    $[9] = source;
    $[10] = toggleDialog;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const openNudge = t5;
  let t6;
  if ($[12] !== openNudge) {
    t6 = () => {
      if (hasOpenedRef.current) {
        return;
      }
      if (isOpenRef.current) {
        pendingOpenRef.current = true;
        return;
      }
      openNudge();
    };
    $[12] = openNudge;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const requestOpen = t6;
  let t7;
  let t8;
  if ($[14] !== isOpen || $[15] !== openNudge) {
    t7 = () => {
      if (!isOpen && pendingOpenRef.current && !hasOpenedRef.current) {
        openNudge();
      }
    };
    t8 = [isOpen, openNudge];
    $[14] = isOpen;
    $[15] = openNudge;
    $[16] = t7;
    $[17] = t8;
  } else {
    t7 = $[16];
    t8 = $[17];
  }
  useEffect(t7, t8);
  let t9;
  if ($[18] !== dismissed || $[19] !== requestOpen) {
    t9 = {
      dismissed,
      requestOpen
    };
    $[18] = dismissed;
    $[19] = requestOpen;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};